/**
 * Экраны 2–3 — сканирование кода и результат.
 *
 * Камера здесь — заглушка, как в прототипе: распознавание QR требует разрешения
 * и библиотеки, а ручной ввод работает всегда и покрывает код под крышкой и из
 * чека. Поле ввода — главный путь, камера появится рядом с ним.
 */
import type { RedeemResult, WeightedPrize } from '@allswipes/client';
import { ApiError } from '@allswipes/client';
import { Badge, Button, Card, Field, Hash, Icon, KeyRow, Sheet, num, ticketChance } from '@allswipes/ui';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { api } from '../api';
import { lastLink } from '../link';
import { ConsentSheet } from '../components/ConsentSheet';
import { Shell } from '../components/Shell';
import { useSession } from '../session';

import { usePlural, useT } from '@allswipes/i18n';

export function ScanScreen() {
  const t = useT();
  const { signedIn, refresh } = useSession();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<RedeemResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Акция попросила данные: показываем экран согласия и после него сами
  // повторяем попытку — код не сгорел, человеку незачем вводить его заново.
  const [consentFor, setConsentFor] = useState<string | null>(null);
  // Акция с розыгрышем просит выбрать цифру: она войдёт в сид вместе с
  // цифрами остальных участников.
  const [picks, setPicks] = useState<number[] | null>(null);

  if (!signedIn) {
    return (
      <Shell title={t('Сканировать')}>
        <Card>
          <div className="h2">{t('Нужно войти')}</div>
          <p className="sub" style={{ marginTop: 6 }}>
            {t('Код привязывается к твоему аккаунту — иначе мы не сможем показать билет и приз.')}
          </p>
          <div style={{ marginTop: 12 }}>
            <Button variant="pri" block onClick={() => navigate('/login?next=/scan')}>
              {t('Войти по почте')}
            </Button>
          </div>
        </Card>
      </Shell>
    );
  }

  const submit = async (value = code, pick = 0) => {
    if (!value.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.redeem(value, pick, lastLink());
      setResult(res);
      setCode('');
      void refresh();
    } catch (err) {
      // 428 — не ошибка, а незаконченный шаг: бренд запросил данные. Акцию
      // сервер называет сам: искать её по коду приложение не может, код чужой
      // до тех пор, пока не погашен.
      if (err instanceof ApiError && err.status === 428 && err.picks?.length) {
        setPicks(err.picks);
        setError(null);
        return;
      }
      if (err instanceof ApiError && err.status === 428 && err.campaignId) {
        setConsentFor(err.campaignId);
        setError(null);
        return;
      }
      setError(err instanceof ApiError ? err.message : t('Не удалось проверить код'));
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <Shell title={result.kind === 'ticket' ? t('Билет получен') : t('Результат')}>
        <ScanResult result={result} onAgain={() => setResult(null)} />
      </Shell>
    );
  }

  const pickSheet = picks ? (
    <PickSheet
      picks={picks}
      busy={busy}
      onClose={() => setPicks(null)}
      onPick={(value) => {
        setPicks(null);
        void submit(code, value);
      }}
    />
  ) : null;

  const sheet = consentFor ? (
    <ConsentSheet
      campaignId={consentFor}
      onClose={() => setConsentFor(null)}
      onDone={() => {
        setConsentFor(null);
        void submit();
      }}
    />
  ) : null;

  return (
    <Shell title={t('Сканировать')} subtitle={t('код под крышкой, на этикетке или в чеке')}>
      <div className="scanbox">
        <div>
          <div className="ic">
            <Icon name="qr" size={22} />
          </div>
          <div className="h2">{t('Камера появится здесь')}</div>
          <p className="sub" style={{ marginTop: 6 }}>
            {t('Пока вводи код вручную или загрузи фото QR — так работает и код под крышкой, и код из чека.')}
          </p>
        </div>
      </div>

      <QrUpload
        onCode={(value) => {
          setCode(value);
          void submit(value);
        }}
        onError={setError}
      />

      <Card>
        <Field label={t('Код с упаковки или из чека')} error={error ?? undefined}>
          <input
            className="input mono codein"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submit();
            }}
            placeholder="CC-0001"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            inputMode="text"
            aria-label={t('Код акции')}
          />
        </Field>
        <Button variant="pri" block disabled={busy || !code.trim()} onClick={() => void submit()}>
          {busy ? t('Проверяем…') : t('Проверить код')}
        </Button>
        <p className="sub" style={{ marginTop: 10 }}>
          {t('Пробелы, дефисы и регистр не важны. Один код принимается один раз.')}
        </p>
      </Card>
      {sheet}
      {pickSheet}
    </Shell>
  );
}

/**
 * Загрузка фото с QR-кодом.
 *
 * Распознаём штатным BarcodeDetector — он есть в Chrome и Android-браузерах и
 * не тянет мегабайт библиотеки ради одной функции. Там, где его нет (Safari,
 * Firefox), честно говорим об этом и оставляем ручной ввод: ввод кода работает
 * везде и всегда, а картинка — это ускорение, а не единственный путь.
 */
function QrUpload({
  onCode,
  onError,
}: {
  onCode: (code: string) => void;
  onError: (message: string) => void;
}) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const supported = typeof window !== 'undefined' && 'BarcodeDetector' in window;

  const read = async (file: File) => {
    setBusy(true);
    try {
      const Detector = (window as unknown as { BarcodeDetector: BarcodeDetectorCtor })
        .BarcodeDetector;
      const detector = new Detector({ formats: ['qr_code'] });
      const bitmap = await createImageBitmap(file);
      const found = await detector.detect(bitmap);
      bitmap.close?.();

      const raw = found[0]?.rawValue?.trim();
      if (!raw) {
        onError(t('На картинке не нашёлся QR-код. Попробуй другое фото или введи код руками.'));
        return;
      }
      onCode(codeFromQr(raw));
    } catch {
      onError(t('Не получилось прочитать картинку. Введи код руками — это всегда работает.'));
    } finally {
      setBusy(false);
    }
  };

  if (!supported) {
    return (
      <Card style={{ marginBottom: 14 }}>
        <div className="row" style={{ gap: 9, alignItems: 'flex-start' }}>
          <Icon name="alert" size={16} />
          <span className="sub">
            {t('Этот браузер не умеет читать QR из картинки. Введи код с упаковки руками — или открой приложение в Chrome.')}
          </span>
        </div>
      </Card>
    );
  }

  return (
    <Card style={{ marginBottom: 14 }}>
      <label className="qrupload">
        <Icon name="upload" size={16} />
        <span>{busy ? t('Читаем картинку…') : t('Загрузить фото QR-кода')}</span>
        <input
          type="file"
          accept="image/*"
          disabled={busy}
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) void read(file);
          }}
        />
      </label>
      <p className="sub" style={{ marginTop: 8 }}>
        {t('Подойдёт снимок крышки, этикетки или чека. Картинка не покидает телефон: код читается прямо здесь, на сервер уходит только он сам.')}
      </p>
    </Card>
  );
}

/** Минимальная часть BarcodeDetector, которой мы пользуемся. */
interface BarcodeDetectorCtor {
  new (options?: { formats?: string[] }): {
    detect: (source: ImageBitmap) => Promise<{ rawValue?: string }[]>;
  };
}

/**
 * Достаёт код из того, что зашито в QR.
 *
 * В QR на упаковке лежит ссылка на страницу акции, а не сам код, — поэтому
 * сначала пробуем вытащить код из адреса, и только потом считаем, что в QR
 * был сам код.
 */
function codeFromQr(raw: string): string {
  try {
    const url = new URL(raw);
    const fromQuery = url.searchParams.get('code') ?? url.searchParams.get('c');
    if (fromQuery) return fromQuery;
    const last = url.pathname.split('/').filter(Boolean).pop();
    if (last) return last;
  } catch {
    // Не ссылка — значит, в QR сам код.
  }
  return raw;
}


function ScanResult({ result, onAgain }: { result: RedeemResult; onAgain: () => void }) {
  const t = useT();
  const { campaign: c } = result;

  if (result.kind === 'ticket' && result.ticket) {
    return (
      <div className="stack">
        {result.replay && (
          <Card>
            <Badge tone="warn">{t('этот код уже был')}</Badge>
            <p className="sub" style={{ marginTop: 8 }}>
              {t('Показываем тот же билет — второй раз код не считается.')}
            </p>
          </Card>
        )}
        <Card>
          <div className="ticket">
            <div className="lab">{t('Твой билет')}</div>
            <div className="num">{result.ticket.number}</div>
            <div className="meta">{c.name}</div>
          </div>
          {result.points > 0 && !result.replay && (
            <div style={{ marginTop: 12, textAlign: 'center' }}>
              <Badge tone="prize">{t('+{n} баллов', { n: result.points })}</Badge>
            </div>
          )}
          <div className="stack" style={{ marginTop: 14 }}>
            <KeyRow label={t('Акция')}>{c.prize_title || c.name}</KeyRow>
            <KeyRow label={t('Победителей')}>{num(c.winner_count)}</KeyRow>
            {ticketChance(c.winner_count, c.tickets) !== '' && (
              <KeyRow label={t('Шанс билета')}>{ticketChance(c.winner_count, c.tickets)}%</KeyRow>
            )}
            {result.commit && (
              <KeyRow label={t('Хеш сида')} mono>
                <Hash value={result.commit} />
              </KeyRow>
            )}
          </div>
          <p className="sub" style={{ marginTop: 12 }}>
            {t('Номер уже в открытом списке участников. Победителей выберет публичный сид в день розыгрыша — результат можно будет пересчитать самому.')}
          </p>
        </Card>
        <div className="row" style={{ gap: 8 }}>
          <Button variant="pri" block onClick={onAgain}>
            {t('Ещё код')}
          </Button>
          <Link to={`/c/${c.id}`} className="btn out block">
            {t('К акции')}
          </Link>
        </div>
      </div>
    );
  }

  if (result.kind === 'instant' && result.outcome) {
    const segments: WeightedPrize[] = c.config.segments ?? c.config.scratch_prizes ?? [];
    return (
      <div className="stack">
        {result.replay && (
          <Card>
            <Badge tone="warn">{t('этот код уже был')}</Badge>
            <p className="sub" style={{ marginTop: 8 }}>{t('Показываем тот же результат.')}</p>
          </Card>
        )}
        {/* Колесо рисуем только там, где исход действительно взвешенный.
            У флеша и у участия без приза крутить нечего. */}
        {segments.length > 0 && <Wheel segments={segments} winner={result.outcome.prize_index} />}

        {result.flash && (
          <Card>
            <div style={{ textAlign: 'center' }}>
              <div className="lab">{t('твоё место')}</div>
              <div className="h1" style={{ marginTop: 6 }}>
                №{result.flash.seq}
              </div>
              <p className="sub" style={{ marginTop: 8 }}>
                {t('из {spots} · занято {taken}', {
                  spots: num(result.flash.spots),
                  taken: num(result.flash.taken),
                })}
              </p>
            </div>
            <p className="sub" style={{ marginTop: 12 }}>
              {t('Место определила очередь, а не случай: его получили первые {spots} по времени подтверждения кода на сервере. Счётчик мест публичный — это и есть проверка.', {
                spots: num(result.flash.spots),
              })}
            </p>
          </Card>
        )}

        {result.collection && (
          <Card>
            <div className="row" style={{ marginBottom: 10 }}>
              <span className="h2">{t('Твой набор')}</span>
              <span className="spacer" />
              <span className="mono faint" style={{ fontSize: 12 }}>
                {result.collection.owned}/{result.collection.total}
              </span>
            </div>
            <div className="cells">
              {result.collection.items.map((item, i) => (
                <div key={i} className={`cell ${item.owned ? 'have' : ''}`}>
                  <span className="ic">
                    <Icon name={item.owned ? 'check' : 'gift'} size={22} />
                  </span>
                  <span className="nm">{item.label}</span>
                </div>
              ))}
            </div>
            {result.collection.just_made_it ? (
              <p className="sub" style={{ marginTop: 10 }}>
                {t('Набор собран — «{prize}» твой. Приз гарантирован за полный набор, без розыгрыша; оформить его можно в «Мои участия».', {
                  prize: result.collection.set_prize ?? '',
                })}
              </p>
            ) : (
              <p className="sub" style={{ marginTop: 10 }}>
                {t('Осталось собрать {left} из {total} — за полный набор выдаётся «{prize}».', {
                  left: result.collection.total - result.collection.owned,
                  total: result.collection.total,
                  prize: result.collection.set_prize ?? '',
                })}
              </p>
            )}
          </Card>
        )}
        <Card>
          <div style={{ textAlign: 'center' }}>
            <div className="lab">{t('Результат')}</div>
            <div className="h1" style={{ marginTop: 6 }}>
              {result.outcome.prize_label}
            </div>
            {result.points > 0 && !result.replay && (
              <div style={{ marginTop: 10 }}>
                <Badge tone="prize">{t('+{n} баллов', { n: result.points })}</Badge>
              </div>
            )}
          </div>
          <div className="stack" style={{ marginTop: 14 }}>
            {result.commit && (
              <KeyRow label={t('Хеш сида')} mono>
                <Hash value={result.commit} />
              </KeyRow>
            )}
            <KeyRow label={t('Алгоритм')}>{result.outcome.algo_version}</KeyRow>
          </div>
          <p className="sub" style={{ marginTop: 12 }}>
            {t('Исход посчитал сервер по сиду, хеш которого опубликован до старта акции. Сам сид раскроется после окончания — тогда результат можно будет пересчитать.')}
          </p>
          <Link
            to={`/verify/${c.id}`}
            className="btn out block"
            style={{ marginTop: 12 }}
          >
            {t('Как это проверить')}
          </Link>
        </Card>
        {result.collection?.set_claim && (
          <Card>
            <div className="h2">{t('Приз за полный набор')}</div>
            <p className="sub" style={{ marginTop: 6 }}>
              {t('{prize} · выдаётся в точке партнёра', {
                    prize: result.collection.set_claim.prize_label,
                  })}
            </p>
            <Link to="/my" className="btn pri block" style={{ marginTop: 12 }}>
              {t('Оформить в «Мои участия»')}
            </Link>
          </Card>
        )}
        {result.claim && (
          <Card>
            <div className="h2">{t('Приз нужно оформить')}</div>
            <p className="sub" style={{ marginTop: 6 }}>
              {result.claim.prize_label} ·{' '}
              {result.claim.fulfil === 'instant' ? t('доступен сразу') : t('выдаётся в точке партнёра')}
            </p>
            <Link to="/my" className="btn pri block" style={{ marginTop: 12 }}>
              {t('Оформить в «Мои участия»')}
            </Link>
          </Card>
        )}
        <Button variant="pri" block onClick={onAgain}>
          {t('Ещё код')}
        </Button>
      </div>
    );
  }

  return (
    <Card>
      <div className="h2">{t('Код принят')}</div>
      <div style={{ marginTop: 12 }}>
        <Button variant="pri" block onClick={onAgain}>
          {t('Ещё код')}
        </Button>
      </div>
    </Card>
  );
}

/**
 * Колесо. Угол поворота вычисляется из уже известного исхода: анимация
 * показывает решение сервера, а не принимает его — клиент не может повлиять на
 * результат, даже если подменит этот код.
 */
function Wheel({ segments, winner }: { segments: WeightedPrize[]; winner: number }) {
  const plural = usePlural();
  const total = segments.reduce((sum, s) => sum + Math.max(0, s.weight), 0) || 1;

  let acc = 0;
  const stops: string[] = [];
  let winnerMid = 0;
  segments.forEach((s, i) => {
    const share = (Math.max(0, s.weight) / total) * 360;
    const shade = 12 + Math.round((i / Math.max(1, segments.length - 1)) * 26);
    stops.push(`hsl(0 0% ${shade}%) ${acc}deg ${acc + share}deg`);
    if (i === winner) winnerMid = acc + share / 2;
    acc += share;
  });

  // Поворачиваем так, чтобы середина выигравшего сектора встала под стрелку.
  const rotation = 360 * 4 - winnerMid;

  return (
    <div className="wheel">
      <div className="needle" />
      <div
        className="disc"
        style={{
          background: `conic-gradient(${stops.join(', ')})`,
          transform: `rotate(${rotation}deg)`,
        }}
      />
      <div className="hub">
        {segments.length} {plural(segments.length, 'сектор', 'сектора', 'секторов')}
      </div>
    </div>
  );
}

/**
 * Выбор цифры участника.
 *
 * Цифра входит в сид розыгрыша вместе с цифрами всех остальных: их сумма даёт
 * множитель, и он фиксируется в списке билетов до того, как объявят публичный
 * сид. Поэтому выбор — настоящий: подогнать его под будущий результат нельзя
 * ни участнику, ни бренду.
 */
function PickSheet({
  picks,
  busy,
  onPick,
  onClose,
}: {
  picks: number[];
  busy: boolean;
  onPick: (value: number) => void;
  onClose: () => void;
}) {
  const t = useT();
  return (
    <Sheet title={t('Выбери цифру')} onClose={onClose}>
      <p className="sub" style={{ marginBottom: 14 }}>
        {t('Твоя цифра складывается с цифрами остальных участников. Из суммы получается множитель, который входит в сид розыгрыша — вместе с публичным тиражом, который объявят после закрытия приёма кодов.')}
      </p>

      <div className="picks">
        {picks.map((value) => (
          <button key={value} type="button" className="pick" disabled={busy} onClick={() => onPick(value)}>
            {value}
          </button>
        ))}
      </div>

      <Card style={{ marginTop: 14 }}>
        <div className="row" style={{ gap: 9, alignItems: 'flex-start' }}>
          <Icon name="shield" size={16} />
          <span className="sub">
            {t('Цифра попадёт в открытый список участников рядом с номером билета. Любой сможет сложить цифры и получить тот же множитель — в этом и смысл: на результат влияют участники, а не организатор.')}
          </span>
        </div>
      </Card>
    </Sheet>
  );
}
